import { useCallback, useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { adminFetch, ApiClientError } from "../lib/apiClient";
import { AdminNav } from "../components/admin/AdminNav";
import type { AdminWebinar, LeadRow, LeadsResponse } from "../types";

function fullName(r: LeadRow): string {
  const name = [r.lastName, r.firstName].filter(Boolean).join(" ");
  return name || "(氏名なし)";
}

function hubspotUrl(portalId: string | null, contactId: string | null): string | null {
  if (!portalId || !contactId) return null;
  return `https://app.hubspot.com/contacts/${portalId}/contact/${contactId}`;
}

export function AdminLeadsPage() {
  const { webinarId } = useParams<{ webinarId: string }>();
  const [leads, setLeads] = useState<LeadRow[]>([]);
  const [portalId, setPortalId] = useState<string | null>(null);
  const [webinarTitle, setWebinarTitle] = useState<string>("");
  const [error, setError] = useState<string | null>(null);

  // フィルター
  const [attendedOnly, setAttendedOnly] = useState(false);
  const [consultationOnly, setConsultationOnly] = useState(false);
  const [hotOnly, setHotOnly] = useState(false);
  const [search, setSearch] = useState("");

  const load = useCallback(async () => {
    if (!webinarId) return;
    try {
      const data = await adminFetch<LeadsResponse>(
        `/api/admin/webinars/${webinarId}/leads`,
      );
      setLeads(data.leads);
      setPortalId(data.portal_id);
      setError(null);
    } catch (err) {
      setError(err instanceof ApiClientError ? err.message : "読み込みに失敗しました");
    }
  }, [webinarId]);

  useEffect(() => {
    load();
  }, [load]);

  // 本番中の新規参加・質問が手動更新なしで出るよう軽くポーリング
  useEffect(() => {
    const id = setInterval(load, 15_000);
    return () => clearInterval(id);
  }, [load]);

  useEffect(() => {
    adminFetch<{ webinars: AdminWebinar[] }>("/api/admin/webinars")
      .then((d) => {
        const w = d.webinars.find((x) => x.id === webinarId);
        if (w) setWebinarTitle(w.title);
      })
      .catch(() => {});
  }, [webinarId]);

  const q = search.trim().toLowerCase();
  const filtered = leads.filter((l) => {
    if (attendedOnly && !l.attended) return false;
    if (consultationOnly && !l.wantsConsultation) return false;
    if (hotOnly && !l.hotLead) return false;
    if (q) {
      const hay = `${fullName(l)} ${l.companyName ?? ""} ${l.email}`.toLowerCase();
      if (!hay.includes(q)) return false;
    }
    return true;
  });

  return (
    <div className="admin-page">
      <AdminNav webinarId={webinarId} current="leads" />
      <header className="admin-header">
        <h1>リード一覧</h1>
        <p className="muted">Webinar: {webinarTitle || webinarId}</p>
      </header>

      <section className="lead-filters">
        <input
          type="search"
          placeholder="氏名・会社・メールで検索"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />
        <label className="checkbox">
          <input
            type="checkbox"
            checked={attendedOnly}
            onChange={(e) => setAttendedOnly(e.target.checked)}
          />
          参加者のみ
        </label>
        <label className="checkbox">
          <input
            type="checkbox"
            checked={consultationOnly}
            onChange={(e) => setConsultationOnly(e.target.checked)}
          />
          相談希望のみ
        </label>
        <label className="checkbox">
          <input
            type="checkbox"
            checked={hotOnly}
            onChange={(e) => setHotOnly(e.target.checked)}
          />
          ホットリードのみ
        </label>
        <span className="muted lead-count">
          {filtered.length} / {leads.length} 件
        </span>
      </section>

      {error && (
        <p className="form-error" role="alert">
          {error}
        </p>
      )}

      <table className="lead-table">
        <thead>
          <tr>
            <th>氏名</th>
            <th>会社</th>
            <th>メール</th>
            <th>参加</th>
            <th>質問数</th>
            <th>最終質問</th>
            <th>最終質問日時</th>
            <th>HubSpot</th>
          </tr>
        </thead>
        <tbody>
          {filtered.length === 0 ? (
            <tr>
              <td colSpan={8} className="empty">
                該当するリードはいません
              </td>
            </tr>
          ) : (
            filtered.map((r) => {
              const url = hubspotUrl(portalId, r.hubspotContactId);
              return (
                <tr key={r.registrationId}>
                  <td>{fullName(r)}</td>
                  <td>{r.companyName ?? "-"}</td>
                  <td>{r.email}</td>
                  <td>
                    {r.attended ? (
                      <span className="lead-attended">参加</span>
                    ) : (
                      <span className="muted">未参加</span>
                    )}
                  </td>
                  <td>
                    {r.questionCount > 0 ? (
                      <Link
                        to={`/admin/webinars/${webinarId}/questions?registration=${r.registrationId}`}
                      >
                        {r.questionCount}
                      </Link>
                    ) : (
                      <span className="muted">0</span>
                    )}
                  </td>
                  <td className="lead-question">
                    {r.lastQuestionId ? (
                      <Link
                        to={`/admin/webinars/${webinarId}/questions?question=${r.lastQuestionId}`}
                      >
                        {r.lastQuestionText}
                      </Link>
                    ) : (
                      "-"
                    )}
                  </td>
                  <td>
                    {r.lastQuestionAt
                      ? new Date(r.lastQuestionAt).toLocaleString("ja-JP")
                      : "-"}
                  </td>
                  <td>
                    {url ? (
                      <a href={url} target="_blank" rel="noreferrer">
                        開く
                      </a>
                    ) : (
                      "-"
                    )}
                  </td>
                </tr>
              );
            })
          )}
        </tbody>
      </table>
    </div>
  );
}
